import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';
import { LoiComparisonFormDto } from '../../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import {
    PriceRow,
    getFieldComment,
    buildConsiderationSummary,
    buildEmptyRowCells,
    getEmployeeColumnVisibility,
    PRICE_ROWS,
    COLUMN_TOOLTIPS,
} from './LoiComparisonRows';
import { SfasFeeParams } from '../../../../slices/marketplace/loi-comparison/consideration-fees';
import { isSfas } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { isHeaderOnlyConsiderationType } from '../../../../slices/marketplace/pricing/models/type-of-consideration.enum';
import { DEEP_DIVE_REGISTRY } from './DeepDiveDetailPanel/detail-config';

export const TAIL_COLUMN_HEADERS = [
    'Fees',
    'Net of Fees',
    'Seller Consideration for Employees',
    'Buyer Consideration for Employees',
    'Net to Owner',
];

export const DETAIL_TAIL_COLUMN_HEADERS = [
    'Fees',
    'Net of Fees',
    'Liability Payoffs',
    'Seller Consideration for Employees',
    'Buyer Consideration for Employees',
    'Net to Owner',
];

export const COLLAPSIBLE_COLUMN_TOKEN = '__pct:';

const PERCENT_OF_TOTAL_LABEL = '% of Total';

export interface ConsiderationSummaryTableProps {
    item: LoiComparisonFormDto;
    levelOfService: number;
    sfasParams?: SfasFeeParams | null;
    showFairValue: boolean;
    renderLabelCells?: boolean;
    showLiabilityPayoffs?: boolean;
    tooltipIdSuffix?: string;
    collapsibleColumns?: Record<string, boolean>;
    onToggleColumn?: (key: string) => void;
    isDeepDive?: boolean;
    selectedRowLabel?: string | null;
    onCellsClick?: (rowLabel: string, buyerId: number) => void;
    headerRow?: (colSpan: number) => React.ReactNode;
    footerRow?: (colSpan: number) => React.ReactNode;
    className?: string;
}

export const ConsiderationSummaryTable = ({
    item,
    levelOfService,
    sfasParams = null,
    showFairValue,
    renderLabelCells = false,
    showLiabilityPayoffs = false,
    tooltipIdSuffix = '',
    collapsibleColumns,
    onToggleColumn,
    isDeepDive = false,
    selectedRowLabel = null,
    onCellsClick,
    headerRow,
    footerRow,
    className,
}: ConsiderationSummaryTableProps) => {
    const { rowsByType, totalCells } = buildConsiderationSummary(item, levelOfService, sfasParams);
    const totalFaceValue = totalCells.faceValue ?? 0;
    const totalFairValue = totalCells.fairValue ?? 0;
    const { showSellerEmployeeColumn, showBuyerEmployeeColumn } = getEmployeeColumnVisibility(item);

    const isCollapsible = collapsibleColumns !== undefined && onToggleColumn !== undefined;
    const tailHeaders = [
        'Fees',
        'Net of Fees',
        ...(showLiabilityPayoffs ? ['Liability Payoffs'] : []),
        ...(showSellerEmployeeColumn ? ['Seller Consideration for Employees'] : []),
        ...(showBuyerEmployeeColumn ? ['Buyer Consideration for Employees'] : []),
        'Net to Owner',
    ];
    const pctFaceHeader = isCollapsible ? `${COLLAPSIBLE_COLUMN_TOKEN}face` : PERCENT_OF_TOTAL_LABEL;
    const pctFairHeader = isCollapsible ? `${COLLAPSIBLE_COLUMN_TOKEN}fair` : PERCENT_OF_TOTAL_LABEL;
    const columnHeaders = [
        'Face Values',
        pctFaceHeader,
        ...(showFairValue ? ['Fair Value', pctFairHeader] : []),
        'Projected Cash Flow',
        ...tailHeaders,
    ];
    const colSpan = columnHeaders.length;
    const showFaceColumn = isCollapsible ? !!collapsibleColumns!.face : true;
    const showFairColumn = isCollapsible ? !!collapsibleColumns!.fair : true;

    const renderHeaderCell = (label: string, idx: number) => {
        if (label.startsWith(COLLAPSIBLE_COLUMN_TOKEN)) {
            const pctKey = label.slice(COLLAPSIBLE_COLUMN_TOKEN.length);
            const visible = collapsibleColumns![pctKey];
            return visible ? (
                <th key={`${label}-${idx}`} className="sub-col-header">
                    <button
                        className="percent-toggle-btn"
                        onClick={() => onToggleColumn!(pctKey)}
                        aria-label="Collapse % column"
                    >
                        <FontAwesomeIcon icon={faMinus} />
                    </button>
                    {PERCENT_OF_TOTAL_LABEL}
                </th>
            ) : (
                <th key={`${label}-${idx}`} className="percent-expander-col">
                    <button
                        className="percent-toggle-btn"
                        onClick={() => onToggleColumn!(pctKey)}
                        aria-label="Expand % column"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </button>
                </th>
            );
        }
        const headerTooltip = label === 'Fees' && !isSfas(levelOfService)
            ? undefined
            : COLUMN_TOOLTIPS[label];
        return (
            <th key={`${label}-${idx}`} className="sub-col-header">
                {label}
                {headerTooltip && (
                    <InfoTooltip
                        text={headerTooltip}
                        idText={`loi-col-tooltip-${idx}-${item.buyerId}${tooltipIdSuffix}`}
                    />
                )}
            </th>
        );
    };

    return (
        <table className={className}>
            <thead>
                {headerRow && headerRow(colSpan)}
                <tr className="sub-col-header-row">
                    {renderLabelCells && <th></th>}
                    {columnHeaders.map((label, idx) => renderHeaderCell(label, idx))}
                </tr>
            </thead>
            <tbody>
                {PRICE_ROWS.map(({ label, type, indented }) => {
                    const isHeaderOnly = isHeaderOnlyConsiderationType(type);
                    const cells = rowsByType[type] ?? buildEmptyRowCells();
                    const isClickable = !!onCellsClick && !isHeaderOnly
                        && (DEEP_DIVE_REGISTRY[label] !== undefined || label === 'Other');
                    const isExpanded = !!onCellsClick && isDeepDive && selectedRowLabel === label;
                    return (
                        <PriceRow
                            key={label}
                            label={label}
                            indented={indented}
                            cells={cells}
                            totalFaceValue={totalFaceValue}
                            totalFairValue={totalFairValue}
                            showFairValue={showFairValue}
                            showFaceColumn={showFaceColumn}
                            showFairColumn={showFairColumn}
                            buyerId={item.buyerId}
                            comment={getFieldComment(item, cells.fieldKey)}
                            isDeepDive={onCellsClick ? isDeepDive : undefined}
                            isClickable={isClickable}
                            isExpanded={isExpanded}
                            onCellsClick={onCellsClick ? (buyerId) => onCellsClick(label, buyerId) : undefined}
                            tooltipIdSuffix={tooltipIdSuffix}
                            showLiabilityPayoffs={showLiabilityPayoffs}
                            showSellerEmployeeColumn={showSellerEmployeeColumn}
                            showBuyerEmployeeColumn={showBuyerEmployeeColumn}
                            isHeaderOnly={isHeaderOnly}
                            renderLabelCell={renderLabelCells}
                        />
                    );
                })}
                <PriceRow
                    label="Total Value"
                    cells={totalCells}
                    totalFaceValue={totalFaceValue}
                    totalFairValue={totalFairValue}
                    showFairValue={showFairValue}
                    showFaceColumn={showFaceColumn}
                    showFairColumn={showFairColumn}
                    buyerId={item.buyerId}
                    isBold
                    tooltipIdSuffix={tooltipIdSuffix}
                    showLiabilityPayoffs={showLiabilityPayoffs}
                    showSellerEmployeeColumn={showSellerEmployeeColumn}
                    showBuyerEmployeeColumn={showBuyerEmployeeColumn}
                    renderLabelCell={renderLabelCells}
                />
                {footerRow && footerRow(colSpan)}
            </tbody>
        </table>
    );
};
